import { formatUserCurrency } from '../../lib/money';
/**
 * User Management Component - Admin Dashboard
 * Allows admins to view, edit, and manage users
 */

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Search,
  Filter,
  MoreVertical,
  Edit2,
  Trash2,
  Shield,
  ShieldOff,
  Ban,
  CheckCircle,
  UserPlus,
  UserCircle,
  Download,
  Mail,
  Phone,
  Calendar,
  ShoppingBag,
  IndianRupee,
  Gift,
  History,
  ArrowUpCircle,
  ArrowDownCircle,
  MinusCircle,
  X
} from 'lucide-react';
import walletService from '../../services/walletService';
import { toast } from 'sonner';
import userService from '../../services/userService';
import { useAuth } from '../../context/AuthContext';
import CreditWalletModal from './CreditWalletModal';
import DebitWalletModal from './DebitWalletModal';

// Role display labels (stored values stay super_admin / admin / user)
const ROLE_LABELS = {
  super_admin: 'Master Control Admin',
  admin: 'Normal Admin',
  user: 'Customer',
};
const roleLabel = (role) => ROLE_LABELS[role] || role;
const isAdminTier = (role) => role === 'admin' || role === 'super_admin';

const UserManagement = () => {
  // Only a Master Control Admin (super_admin) may assign roles or change
  // statuses / edit other admins. Normal Admins get a read-mostly user list.
  const { isSuperAdmin, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCreditWalletModal, setShowCreditWalletModal] = useState(false);
  const [showDebitWalletModal, setShowDebitWalletModal] = useState(false);
  const [showWalletHistoryModal, setShowWalletHistoryModal] = useState(false);
  const [walletHistory, setWalletHistory] = useState(null);
  const [walletHistoryLoading, setWalletHistoryLoading] = useState(false);
  const walletHistoryCancelRef = useRef(null);
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const [stats, setStats] = useState({ total: 0, active: 0, admins: 0, newThisMonth: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const USERS_PER_PAGE = 15;
  const actionMenuRef = useRef(null);

  // Close action menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target)) {
        setActionMenuOpen(null);
      }
    };
    if (actionMenuOpen !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [actionMenuOpen]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
    fetchUsers(1);
    fetchStats();
  }, [search, roleFilter, statusFilter]);

  useEffect(() => {
    fetchUsers(currentPage);
  }, [currentPage]);

  const fetchUsers = async (page = currentPage) => {
    setLoading(true);
    const result = await userService.getUsers({ 
      search, 
      role: roleFilter, 
      status: statusFilter,
      page,
      limit: USERS_PER_PAGE
    });
    if (result.success) {
      setUsers(result.data.users);
      setTotalPages(result.data.pagination.pages);
      setTotalUsers(result.data.pagination.total);
    }
    setLoading(false);
  };

  const fetchStats = async () => {
    const result = await userService.getStats();
    if (result.success) {
      setStats(result.data);
    }
  };

  const handleOpenWalletHistory = async (user) => {
    // Cancel any in-flight fetch from a previously opened history modal.
    if (walletHistoryCancelRef.current) walletHistoryCancelRef.current();
    let cancelled = false;
    walletHistoryCancelRef.current = () => { cancelled = true; };

    setSelectedUser(user);
    setWalletHistory(null);
    setShowWalletHistoryModal(true);
    setWalletHistoryLoading(true);
    try {
      const result = await walletService.getAdminWalletHistory(user.id);
      if (cancelled) return;
      if (result.success) {
        setWalletHistory(result.data);
      } else {
        toast.error(result.error || 'Failed to load wallet history');
        setShowWalletHistoryModal(false);
      }
    } catch {
      if (cancelled) return;
      toast.error('Failed to load wallet history');
      setShowWalletHistoryModal(false);
    } finally {
      if (!cancelled) setWalletHistoryLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    const result = await userService.changeRole(userId, newRole);
    if (result.success) {
      toast.success(`Role updated to ${roleLabel(newRole)}`);
      fetchUsers();
      fetchStats();
    } else {
      toast.error(result.error || 'Failed to change role');
    }
    setActionMenuOpen(null);
  };

  const handleStatusChange = async (userId, newStatus) => {
    const result = await userService.changeStatus(userId, newStatus);
    if (result.success) {
      fetchUsers();
      fetchStats();
    } else {
      toast.error(result.error || 'Failed to change status');
    }
    setActionMenuOpen(null);
  };

  const handleDelete = async () => {
    if (selectedUser) {
      const result = await userService.deleteUser(selectedUser.id);
      if (result.success) {
        toast.success('User deleted');
        fetchUsers();
        fetchStats();
        setShowDeleteConfirm(false);
        setSelectedUser(null);
      } else {
        toast.error(result.error || 'Failed to delete user');
      }
    }
  };

  const handleUpdateUser = async (updates) => {
    if (selectedUser) {
      const result = await userService.updateUser(selectedUser.id, updates);
      if (result.success) {
        toast.success('User updated');
        fetchUsers();
        fetchStats();
        setShowEditModal(false);
        setSelectedUser(null);
      } else {
        toast.error(result.error || 'Failed to update user');
      }
    }
  };

  const handleAddUser = async (userData) => {
    const result = await userService.createUser(userData);
    if (result.success) {
      fetchUsers();
      fetchStats();
      setShowAddModal(false);
    }
    return result;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata',
    });
  };


  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700';
      case 'inactive': return 'bg-gray-100 text-gray-700';
      case 'suspended': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'super_admin': return 'bg-amber-100 text-amber-700';
      case 'admin': return 'bg-purple-100 text-purple-700';
      default: return 'bg-blue-100 text-blue-700';
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">User Management</h1>
          <p className="text-gray-600 text-sm sm:text-base">Manage users and their permissions</p>
        </div>
        <button 
          onClick={() => setShowAddModal(true)}
          className="flex items-center justify-center gap-2 bg-[#548B2E] hover:bg-[#3f6b20] text-white px-4 py-2 rounded-lg font-medium transition-colors w-full sm:w-auto"
        >
          <UserPlus className="w-5 h-5" />
          Add User
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <ShoppingBag className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
              <p className="text-gray-600 text-sm">Total Users</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-800">{stats.active}</p>
              <p className="text-gray-600 text-sm">Active Users</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Shield className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-800">{stats.admins}</p>
              <p className="text-gray-600 text-sm">Admins</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-800">{stats.newThisMonth}</p>
              <p className="text-gray-600 text-sm">New This Month</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, mobile or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
            >
              <option value="">All Roles</option>
              <option value="super_admin">Master Control Admin</option>
              <option value="admin">Normal Admin</option>
              <option value="user">Customer</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
            <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-sm">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#548B2E] mx-auto"></div>
            <p className="text-gray-500 mt-2">Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500">No users found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 lg:px-6 py-3 text-xs lg:text-sm font-medium text-gray-600">User</th>
                <th className="text-left px-4 lg:px-6 py-3 text-xs lg:text-sm font-medium text-gray-600">Contact</th>
                <th className="text-left px-4 lg:px-6 py-3 text-xs lg:text-sm font-medium text-gray-600">Role</th>
                <th className="text-left px-4 lg:px-6 py-3 text-xs lg:text-sm font-medium text-gray-600">Status</th>
                <th className="text-left px-4 lg:px-6 py-3 text-xs lg:text-sm font-medium text-gray-600">Orders</th>
                <th className="text-left px-4 lg:px-6 py-3 text-xs lg:text-sm font-medium text-gray-600">Total Spent</th>
                <th className="text-left px-4 lg:px-6 py-3 text-xs lg:text-sm font-medium text-gray-600">Joined</th>
                <th className="text-right px-4 lg:px-6 py-3 text-xs lg:text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map(user => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {user.avatar && !user.avatar.includes('dicebear.com') ? (
                        <img src={user.avatar} alt={user.name} className="w-10 h-10 rounded-full bg-gray-100" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-[#f2f8eb] flex items-center justify-center">
                          <UserCircle className="w-5 h-5 text-[#548B2E]" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-gray-800">{user.name}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      <span className="text-sm text-gray-600 flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {user.phone || 'N/A'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}>
                      {roleLabel(user.role)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(user.status)}`}>
                      {user.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-600">{user.ordersCount}</td>
                  <td className="px-6 py-4 text-gray-600">{formatUserCurrency(user.totalSpent)}</td>
                  <td className="px-6 py-4 text-gray-600 text-sm">{formatDate(user.createdAt)}</td>
                  <td className="px-6 py-4 text-right relative">
                    <div ref={actionMenuOpen === user.id ? actionMenuRef : null}>
                    <button 
                      onClick={() => setActionMenuOpen(actionMenuOpen === user.id ? null : user.id)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <MoreVertical className="w-4 h-4 text-gray-600" />
                    </button>
                    
                    {/* Action Menu */}
                    {actionMenuOpen === user.id && (
                      <div className="absolute right-6 bottom-8 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                        {(isSuperAdmin || !isAdminTier(user.role) || user.id === currentUser?.id) && (
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setShowEditModal(true);
                              setActionMenuOpen(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                          >
                            <Edit2 className="w-4 h-4" />
                            Edit User
                          </button>
                        )}

                        {/* Role assignment — Master Control Admins only */}
                        {isSuperAdmin && (
                          <>
                            {user.role !== 'super_admin' && (
                              <button
                                onClick={() => handleRoleChange(user.id, 'super_admin')}
                                className="w-full px-4 py-2 text-left text-sm text-amber-700 hover:bg-amber-50 flex items-center gap-2"
                              >
                                <Shield className="w-4 h-4" />
                                {user.role === 'admin' ? 'Promote to Master Control Admin' : 'Make Master Control Admin'}
                              </button>
                            )}
                            {user.role !== 'admin' && (
                              <button
                                onClick={() => handleRoleChange(user.id, 'admin')}
                                className="w-full px-4 py-2 text-left text-sm text-purple-700 hover:bg-purple-50 flex items-center gap-2"
                              >
                                <Shield className="w-4 h-4" />
                                {user.role === 'super_admin' ? 'Change to Normal Admin' : 'Make Normal Admin'}
                              </button>
                            )}
                            {isAdminTier(user.role) && (
                              <button
                                onClick={() => handleRoleChange(user.id, 'user')}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                              >
                                <ShieldOff className="w-4 h-4" />
                                Remove Admin (make Customer)
                              </button>
                            )}
                          </>
                        )}

                        {/* Status — Master Control Admins only */}
                        {isSuperAdmin && (
                          user.status === 'active' ? (
                            <button
                              onClick={() => handleStatusChange(user.id, 'suspended')}
                              className="w-full px-4 py-2 text-left text-sm text-orange-600 hover:bg-orange-50 flex items-center gap-2"
                            >
                              <Ban className="w-4 h-4" />
                              Suspend User
                            </button>
                          ) : (
                            <button
                              onClick={() => handleStatusChange(user.id, 'active')}
                              className="w-full px-4 py-2 text-left text-sm text-green-600 hover:bg-green-50 flex items-center gap-2"
                            >
                              <CheckCircle className="w-4 h-4" />
                              Activate User
                            </button>
                          )
                        )}

                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setShowCreditWalletModal(true);
                            setActionMenuOpen(null);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-[#548B2E] hover:bg-green-50 flex items-center gap-2"
                        >
                          <Gift className="w-4 h-4" />
                          Credit BMF Coins
                        </button>
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setShowDebitWalletModal(true);
                            setActionMenuOpen(null);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                        >
                          <MinusCircle className="w-4 h-4" />
                          Debit BMF Coins
                        </button>
                        <button
                          onClick={() => {
                            setActionMenuOpen(null);
                            handleOpenWalletHistory(user);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-amber-700 hover:bg-amber-50 flex items-center gap-2"
                        >
                          <History className="w-4 h-4" />
                          BMF Coins History
                        </button>
                        {(isSuperAdmin || !isAdminTier(user.role)) && (
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setShowDeleteConfirm(true);
                              setActionMenuOpen(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete User
                          </button>
                        )}
                      </div>
                    )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-4 bg-white rounded-xl shadow-sm mt-2">
          <p className="text-sm text-gray-500">
            Showing {((currentPage - 1) * USERS_PER_PAGE) + 1}–{Math.min(currentPage * USERS_PER_PAGE, totalUsers)} of {totalUsers} users
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                acc.push(p);
                return acc;
              }, [])
              .map((p, idx) =>
                p === '...' ? (
                  <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">…</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`px-3 py-1.5 text-sm rounded-lg border ${
                      currentPage === p
                        ? 'bg-[#548B2E] text-white border-[#548B2E]'
                        : 'border-gray-200 hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    {p}
                  </button>
                )
              )}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Credit Wallet Modal */}
      {showCreditWalletModal && selectedUser && (
        <CreditWalletModal
          user={selectedUser}
          onClose={() => { setShowCreditWalletModal(false); setSelectedUser(null); }}
        />
      )}

      {/* Debit Wallet Modal (BMF-BRD-2026-014) */}
      {showDebitWalletModal && selectedUser && (
        <DebitWalletModal
          user={selectedUser}
          onClose={() => { setShowDebitWalletModal(false); setSelectedUser(null); }}
          onDebited={() => fetchUsers()}
        />
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <EditUserModal
          user={selectedUser}
          isSuperAdmin={isSuperAdmin}
          onClose={() => {
            setShowEditModal(false);
            setSelectedUser(null);
          }}
          onSave={handleUpdateUser}
        />
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <AddUserModal
          isSuperAdmin={isSuperAdmin}
          onClose={() => setShowAddModal(false)}
          onSave={handleAddUser}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-gray-800 mb-2">Delete User</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete <strong>{selectedUser.name}</strong>? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button 
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setSelectedUser(null);
                }}
                className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button 
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BMF Coins Transaction History Modal */}
      {showWalletHistoryModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[85vh]">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-200 flex-shrink-0">
              <div>
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <History className="w-5 h-5 text-amber-600" />
                  BMF Coins History
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">{selectedUser.name}</p>
              </div>
              <button
                onClick={() => {
                  if (walletHistoryCancelRef.current) { walletHistoryCancelRef.current(); walletHistoryCancelRef.current = null; }
                  setShowWalletHistoryModal(false); setWalletHistory(null); setSelectedUser(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Balance pill */}
            {walletHistory && (
              <div className="px-5 py-3 bg-amber-50 border-b border-amber-100 flex-shrink-0 flex items-center justify-between">
                <span className="text-sm text-amber-700 font-medium">Current Balance</span>
                <span className="text-lg font-bold text-amber-700">₹{Number(walletHistory.balance || 0).toFixed(2)}</span>
              </div>
            )}

            {/* Transaction list */}
            <div className="flex-1 overflow-y-auto">
              {walletHistoryLoading && (
                <div className="flex items-center justify-center py-16 text-gray-400">
                  <div className="w-6 h-6 border-2 border-gray-200 border-t-amber-500 rounded-full animate-spin mr-3" />
                  Loading transactions…
                </div>
              )}
              {!walletHistoryLoading && walletHistory?.transactions?.length === 0 && (
                <div className="py-16 text-center text-gray-400 text-sm">No transactions found.</div>
              )}
              {!walletHistoryLoading && walletHistory?.transactions?.length > 0 && (
                <ul className="divide-y divide-gray-100">
                  {walletHistory.transactions.map((txn) => {
                    const isCredit = txn.type === 'credit' || txn.type === 'admin_credit';
                    const typeLabel = txn.type === 'admin_credit' ? 'Admin Credit'
                      : txn.type === 'credit' ? 'Top-up'
                      : 'Payment';
                    const typeBg = txn.type === 'admin_credit' ? 'bg-amber-100 text-amber-700'
                      : txn.type === 'credit' ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700';
                    return (
                      <li key={txn.id} className="flex items-center gap-3 px-5 py-3.5">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isCredit ? 'bg-green-100' : 'bg-red-100'}`}>
                          {isCredit
                            ? <ArrowUpCircle className="w-4 h-4 text-green-600" />
                            : <ArrowDownCircle className="w-4 h-4 text-red-600" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${typeBg}`}>{typeLabel}</span>
                            {txn.description && (
                              <p className="text-sm text-gray-700 truncate">{txn.description}</p>
                            )}
                          </div>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {new Date(txn.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: true, day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                        <span className={`text-sm font-bold flex-shrink-0 ${isCredit ? 'text-green-600' : 'text-red-600'}`}>
                          {isCredit ? '+' : '−'}₹{Number(txn.amount).toFixed(2)}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Edit User Modal Component
//
// Personal credentials (name / phone / email / address) are READ-ONLY for
// admins (BMF-16) — shown for reference but never editable. Only a Master
// Control Admin can change a user's role/status.
const EditUserModal = ({ user, isSuperAdmin, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    role: user.role,
    status: user.status,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Only role/status are editable (Master Control Admin). Personal
    // credentials are never sent.
    onSave({ role: formData.role, status: formData.status });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-800">{isSuperAdmin ? 'Manage User' : 'User Details'}</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Personal credentials — read-only for admins (BMF-16) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <p className="px-4 py-3 bg-gray-50 rounded-lg text-gray-600 text-sm break-words">{user.name || '—'}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <p className="px-4 py-3 bg-gray-50 rounded-lg text-gray-600 text-sm break-words">{user.email || '—'}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <p className="px-4 py-3 bg-gray-50 rounded-lg text-gray-600 text-sm break-words">{user.phone || '—'}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
            <p className="px-4 py-3 bg-gray-50 rounded-lg text-gray-600 text-sm break-words whitespace-pre-wrap">{user.address || '—'}</p>
          </div>

          <p className="text-xs text-gray-500">
            A user's personal details (name, email, phone, address) are read-only and can only be changed by the user themselves.
          </p>

          {isSuperAdmin ? (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
                >
                  <option value="user">Customer</option>
                  <option value="admin">Normal Admin</option>
                  <option value="super_admin">Master Control Admin</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
            </div>
          ) : (
            <p className="text-xs text-gray-500">
              Only a Master Control Admin can change a user's role or status.
            </p>
          )}

          <div className="flex gap-3 justify-end pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Close
            </button>
            {isSuperAdmin && (
              <button
                type="submit"
                className="px-4 py-2 bg-[#548B2E] text-white rounded-lg hover:bg-[#3f6b20]"
              >
                Save Changes
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

EditUserModal.propTypes = {
  user: PropTypes.shape({
    name: PropTypes.string, phone: PropTypes.string, address: PropTypes.string,
    role: PropTypes.string, status: PropTypes.string, email: PropTypes.string,
  }),
  onClose: PropTypes.func,
  onSave: PropTypes.func,
};

// Add User Modal Component
const AddUserModal = ({ isSuperAdmin, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    address: '',
    role: 'user',
    status: 'active'
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    if (!formData.name || !formData.email || !formData.password) {
      setError('Name, email and password are required');
      setLoading(false);
      return;
    }
    
    const result = await onSave(formData);
    if (!result.success) {
      setError(result.error || 'Failed to create user');
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-800">Add New User</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
              placeholder="Enter full name"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
              placeholder="Enter email address"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
              placeholder="Enter password"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
              placeholder="Enter phone number"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
            <textarea
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
              rows={2}
              placeholder="Enter address"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
              >
                <option value="user">Customer</option>
                {isSuperAdmin && <option value="admin">Normal Admin</option>}
                {isSuperAdmin && <option value="super_admin">Master Control Admin</option>}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          
          <div className="flex gap-3 justify-end pt-4">
            <button 
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit"
              className="px-4 py-2 bg-[#548B2E] text-white rounded-lg hover:bg-[#3f6b20] disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

AddUserModal.propTypes = { onClose: PropTypes.func, onSave: PropTypes.func };

export default UserManagement;
